import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export const DEFAULT_TIME_ZONE = 'Asia/Shanghai'

/** Merges Tailwind classes so a caller's class can always override a default. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Renders a UTC timestamp in the user's timezone.
 * §5.8: dates display local, tooltips carry UTC, the API always speaks UTC.
 */
export function formatDateTime(iso?: string | null, timeZone?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    timeZone: timeZone || DEFAULT_TIME_ZONE,
  }).format(d)
}

/**
 * Same as formatDateTime but to the second.
 *
 * For anything polled faster than once a minute — a drop watch runs every few
 * seconds — minute precision renders consecutive rows identically, and a list
 * whose entries cannot be told apart cannot show an interval either.
 */
export function formatDateTimeSeconds(iso?: string | null, timeZone?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
    timeZone: timeZone || DEFAULT_TIME_ZONE,
  }).format(d)
}

export function formatUTC(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC'
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d)
}

/** Human relative time, used for freshness ("最后确认") columns. */
export function relativeTime(iso?: string | null): string {
  if (!iso) return '从未'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '从未'
  const seconds = Math.round((Date.now() - then) / 1000)
  const abs = Math.abs(seconds)
  const rtf = new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' })
  if (abs < 60) return rtf.format(-seconds, 'second')
  if (abs < 3600) return rtf.format(-Math.round(seconds / 60), 'minute')
  if (abs < 86400) return rtf.format(-Math.round(seconds / 3600), 'hour')
  if (abs < 2592000) return rtf.format(-Math.round(seconds / 86400), 'day')
  return rtf.format(-Math.round(seconds / 2592000), 'month')
}

export function formatNumber(n?: number | null, opts?: Intl.NumberFormatOptions): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('zh-CN', opts).format(n)
}

/** Compact form for very large candidate-space estimates. */
export function formatCompact(n?: number | null): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n >= 1e15) return n.toExponential(2)
  return new Intl.NumberFormat('zh-CN', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}

export function formatDuration(seconds?: number | null): string {
  if (seconds == null || Number.isNaN(seconds)) return '—'
  if (seconds < 60) return `${Math.round(seconds)} 秒`
  if (seconds < 3600) return `${Math.round(seconds / 60)} 分钟`
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} 小时`
  return `${(seconds / 86400).toFixed(1)} 天`
}

/** Formats a limit where -1 means unlimited. */
export function formatLimit(limit: number): string {
  return limit < 0 ? '不限' : formatNumber(limit)
}
